import React, { useEffect } from 'react';
import { ShapeStream, Shape } from '@electric-sql/client';
import { useShape } from '@electric-sql/react';
import { HeadlineGroup, Headline, Insight, HeadlineGroupStat } from '../types';
import { useStore } from '../store/index';
import { useAuth } from '../contexts/AuthContext';

// Create a React context for data
export const DataContext = React.createContext<{
  performVectorSearch: (query: string) => Promise<HeadlineGroupStat[]>;
} | null>(null);

// Helper function to generate a random embedding vector of specified dimension
const generateRandomVector = (dimension: number): number[] => {
  const vector = [];
  for (let i = 0; i < dimension; i++) {
    vector.push((Math.random() * 2) - 1); // Random values between -1 and 1
  }
  return vector;
};

// Helper function to normalize a vector (make its magnitude = 1)
const normalizeVector = (vector: number[]): number[] => {
  const magnitude = Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
  return vector.map(val => val / magnitude);
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ELECTRIC_URL = import.meta.env.VITE_ELECTRIC_URL ?? 'http://localhost:3000';
  
  const {
    setHeadlineGroups,
    setHeadlines,
    setInsights,
    setHeadlineGroupStats,
    setError,
    setIsLoading,
    setUserProfile,
    setStreams,
    setWorkspaces,
    setSelectedStreamId,
    setSelectedWorkspaceId,
    setIsStreamLoading,
    streams,
    selectedHeadlineGroup,
    headlineGroupStats,
  } = useStore();
  
  const { userProfile: authUserProfile, isAuthenticated } = useAuth();
  
  // Use useShape for headline group stats - only when authenticated
  const headlineGroupStatsResult = useShape<HeadlineGroupStat & Record<string, unknown>>({
    url: `${ELECTRIC_URL}/v1/shape`,
    params: { 
      table: 'headline_group_stats',
      columns: ['headline_group_id', 'text_main', 'text_context', 'sentiment_score', 'impact_score', 'embedding', 'created_at', 'updated_at'],
      // Don't filter by headline_group_id so we can search across all stats
    },
    subscribe: isAuthenticated // Only subscribe when authenticated
  });

  // Function to calculate cosine similarity between two vectors
  const cosineSimilarity = (vecA: number[], vecB: number[]): number => {
    if (vecA.length !== vecB.length) {
      throw new Error('Vectors must have the same length');
    }
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }
    
    if (normA === 0 || normB === 0) {
      return 0; // Handle zero vectors
    }
    
    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
  };
  
  // Function to perform vector similarity search
  const performVectorSearch = async (query: string): Promise<HeadlineGroupStat[]> => {
    try {
      console.log(`[App] Performing vector search for query: "${query}"`);
      
      // Generate a random normalized vector of dimension 1536 for testing
      const queryVector = normalizeVector(generateRandomVector(1536));
      
      console.log('[App] Using random vector for search:', queryVector.slice(0, 5), '...');
      
      // In a real implementation, we would generate an embedding from the query text
      // using an embedding model like OpenAI's text-embedding-ada-002
      
      if (headlineGroupStats.length === 0) {
        console.log('[App] No headline group stats available for search');
        return [];
      }
      
      // Filter out stats without embeddings
      const statsWithEmbeddings = headlineGroupStats.filter(stat => 
        stat.embedding && Array.isArray(stat.embedding) && stat.embedding.length > 0
      );
      
      if (statsWithEmbeddings.length === 0) {
        console.log('[App] No headline group stats with embeddings available');
        return [];
      }
      
      // Calculate similarity scores for each stat
      const scoredStats = statsWithEmbeddings.map(stat => ({
        stat,
        similarity: cosineSimilarity(queryVector, stat.embedding)
      }));
      
      // Sort by similarity score (highest first)
      scoredStats.sort((a, b) => b.similarity - a.similarity);
      
      // Take top 5 results
      const topResults = scoredStats.slice(0, 5).map(item => item.stat);
      
      console.log(`[App] Vector search returned ${topResults.length} results`);
      return topResults;
    } catch (error) {
      console.error('[App] Error performing vector search:', error);
      setError(error instanceof Error ? error : new Error('Vector search failed'));
      return [];
    }
  };

  // Sync userProfile from AuthContext to store
  useEffect(() => {
    if (authUserProfile && isAuthenticated) {
      console.log('Syncing userProfile to store:', authUserProfile);
      setUserProfile(authUserProfile);
      
      // When authenticated, replace any default streams with user's streams
      if (authUserProfile.streams && authUserProfile.streams.length > 0) {
        console.log('Replacing default streams with user streams after authentication');
        setStreams(authUserProfile.streams);
        
        // Set the selected stream to the first user stream
        const firstStreamId = authUserProfile.streams[0].stream_id;
        setSelectedStreamId(firstStreamId);
      }
      
      // Set workspaces from authenticated user profile
      if (authUserProfile.workspaces && authUserProfile.workspaces.length > 0) {
        setWorkspaces(authUserProfile.workspaces);
        setSelectedWorkspaceId(authUserProfile.workspaces[0].workspace_id);
      }
    }
  }, [authUserProfile, isAuthenticated, setUserProfile, setStreams, setSelectedStreamId, setWorkspaces, setSelectedWorkspaceId]);

  // Initialize streams and workspaces based on userProfile
  useEffect(() => {
    // Only run this initialization if we're not already authenticated
    // This prevents overriding the authenticated user's streams with stored data
    if (!isAuthenticated) {
      const storedProfile = localStorage.getItem('user_profile');
      if (storedProfile) {
        try {
          const profile = JSON.parse(storedProfile);
          console.log('Initializing with stored profile:', profile);
          setUserProfile(profile);
          
          // Log stream and workspace names from stored data
          console.log('Stored stream names:', profile.streams.map((s: { stream_topic: string }) => s.stream_topic));
          console.log('Stored workspace names:', profile.workspaces.map((w: { workspace_name: string }) => w.workspace_name));
          
          // Set the complete data in the store
          setStreams(profile.streams);
          setWorkspaces(profile.workspaces);

          // Set default selections if available
          if (profile.streams.length > 0) {
            setSelectedStreamId(profile.streams[0].stream_id);
          }
          if (profile.workspaces.length > 0) {
            setSelectedWorkspaceId(profile.workspaces[0].workspace_id);
          }
        } catch (error) {
          console.error('Failed to parse stored profile:', error);
        }
      }
    }
  }, [isAuthenticated, setUserProfile, setStreams, setWorkspaces, setSelectedStreamId, setSelectedWorkspaceId]);

  // Replace the static data subscriptions useEffect with useShape hooks

  // Use useShape for headlines
  const headlinesResult = useShape<Headline & Record<string, unknown>>({
    url: `${ELECTRIC_URL}/v1/shape`,
    params: { 
      table: 'headlines',
      columns: ['headline_group_id', 'content_id', 'url', 'title', 'published_date', 'text_context']
    },
    subscribe: true
  });

  // Use useShape for insights  
  const insightsResult = useShape<Insight & Record<string, unknown>>({
    url: `${ELECTRIC_URL}/v1/shape`,
    params: { 
      table: 'insights',
      columns: ['insight_id', 'title', 'summary', 'text_main', 'headline_group_ids', 'created_at', 'is_questions']
    },
    subscribe: true
  });

  // Log when selected headline group changes
  useEffect(() => {
    if (isAuthenticated && selectedHeadlineGroup) {
      console.log('[App] Selected headline group changed to ID:', selectedHeadlineGroup.headline_group_id);
      // We're not filtering the stats by headline_group_id in the shape query anymore
      // Instead, we'll filter them in the UI components that need to display stats for a specific headline group
    }
  }, [isAuthenticated, selectedHeadlineGroup]);

  // Update store when data changes
  useEffect(() => {
    if (!headlinesResult.isLoading && !insightsResult.isLoading) {
      setHeadlines(headlinesResult.data);
      setInsights(insightsResult.data);
      setIsLoading(false);
    }
  }, [headlinesResult.data, insightsResult.data]);

  // Update store with headline group stats when authenticated
  useEffect(() => {
    if (isAuthenticated && !headlineGroupStatsResult.isLoading && headlineGroupStatsResult.data) {
      console.log('[App] Received headline group stats:', headlineGroupStatsResult.data);
      // Cast the data to the correct type
      const typedStats = headlineGroupStatsResult.data as unknown as HeadlineGroupStat[];
      setHeadlineGroupStats(typedStats);
    }
  }, [isAuthenticated, headlineGroupStatsResult.data, setHeadlineGroupStats]);

  // Handle errors
  useEffect(() => {
    if (headlinesResult.isError) {
      console.error('Headlines stream error:', headlinesResult.error);
      setError(headlinesResult.error instanceof Error ? headlinesResult.error : new Error('Headlines stream error'));
    }
    if (insightsResult.isError) {
      console.error('Insights stream error:', insightsResult.error); 
      setError(insightsResult.error instanceof Error ? insightsResult.error : new Error('Insights stream error'));
    }
    if (isAuthenticated && headlineGroupStatsResult.isError) {
      console.error('Headline group stats stream error:', headlineGroupStatsResult.error);
      setError(headlineGroupStatsResult.error instanceof Error ? headlineGroupStatsResult.error : new Error('Headline group stats stream error'));
    }
  }, [headlinesResult.error, insightsResult.error, isAuthenticated, headlineGroupStatsResult.error]);

  // Initialize dynamic HeadlineGroups subscription
  useEffect(() => {
    let currentStreamId: number | null = null;
    let currentShape: {
      shape: Shape<HeadlineGroup & Record<string, unknown>>;
      unsubscribe: () => void;
      abortController: AbortController;
    } | null = null;
    let isMounted = true;

    // Function to cleanup existing subscription
    function cleanupCurrentShape() {
      if (currentShape) {
        // Abort the controller to stop the fetch/stream
        currentShape.abortController.abort();

        // Unsubscribe from the shape subscription
        currentShape.unsubscribe();

        // Then unsubscribe from the shape itself
        currentShape.shape.unsubscribeAll();

        // Finally unsubscribe from the underlying stream
        currentShape.shape.stream.unsubscribeAll();

        currentShape = null;
      }
    }

    // Function to create and subscribe to headlineGroups stream
    async function subscribeToHeadlineGroups(streamId: number | null) {
      console.log('[App] Setting up subscription for stream:', streamId);
      setIsStreamLoading(true);
      
      try {
        // Clean up existing subscription and stream
        cleanupCurrentShape();

        // If component unmounted during cleanup, don't create new subscription
        if (!isMounted) return;

        // Create a new abort controller for each new subscription
        const abortController = new AbortController();

        const headlineGroupsStream = new ShapeStream<HeadlineGroup & Record<string, unknown>>({
          url: `${ELECTRIC_URL}/v1/shape`,
          params: { 
            table: 'headline_groups',
            where: streamId ? `stream_ids @> ARRAY[${streamId}]` : undefined,
            columns: ['headline_group_id', 'headline_group_name', 'created_at', 'summary', 'stream_ids']
          },
          subscribe: true,
          signal: abortController.signal, // Pass the signal for abort capability
          onError: (err) => {
            if (isMounted) {
              console.error('[App] HeadlineGroups stream error:', err);
              setError(err instanceof Error ? err : new Error('Stream error'));
              setIsStreamLoading(false);
            }
          }
        });

        const headlineGroupsShape = new Shape(headlineGroupsStream);
        
        // Only set up new subscription if we're still on the same stream
        // and component is still mounted
        if (currentStreamId === streamId && isMounted) {
          const unsubscribe = headlineGroupsShape.subscribe(({ rows }) => {
            if (isMounted && currentStreamId === streamId) {
              console.log('[App] Received headline groups for stream:', streamId, rows);
              setHeadlineGroups(rows);
              setIsStreamLoading(false);
            }
          });

          currentShape = { shape: headlineGroupsShape, unsubscribe, abortController };
        } else {
          // Clean up if stream changed during setup
          headlineGroupsStream.unsubscribeAll();
        }
      } catch (error) {
        if (isMounted) {
          console.error('[App] Error creating headline groups stream:', error);
          setError(error instanceof Error ? error : new Error('Failed to create headline groups stream'));
          setIsStreamLoading(false);
        }
      }
    }

    // Subscribe to store changes for stream ID updates
    const unsubscribeFromStore = useStore.subscribe((state) => {
      const newStreamId = state.selectedStreamId;
      if (newStreamId !== currentStreamId) {
        console.log('[App] Stream changed from', currentStreamId, 'to', newStreamId);
        currentStreamId = newStreamId;
        subscribeToHeadlineGroups(newStreamId);
      }
    });

    // Initial subscription
    const initializeHeadlineGroups = async () => {
      const initialStreamId = useStore.getState().selectedStreamId;
      currentStreamId = initialStreamId;
      await subscribeToHeadlineGroups(initialStreamId);
    };

    initializeHeadlineGroups();

    // Clean up on unmount
    return () => {
      console.log('[App] Cleaning up headline groups subscription');
      isMounted = false;
      cleanupCurrentShape();
      unsubscribeFromStore();
    };
  }, []); // Only run once on mount

  return (
    <DataContext.Provider value={{ performVectorSearch }}>
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider; 